// Native React renderers for visual types that don't ship a vendored Plotly
// class. The heavyweight ones now live in their own files:
//   Cards   → CardsVisual.tsx       Feeds  → FeedsVisual.tsx
//   Tree    → TreeOrgChart.tsx      Grid   → GridTable.tsx (ProPivot flat)
//   Pivot   → PivotVisual.tsx       Gantt  → GanttChart.tsx
//   Map     → MapVisual.tsx         Combo  → ComboChart.tsx
// This file keeps the lighter renderers (Html, Calendar) plus the
// dependency-free cross-tab PivotTable that PivotVisual falls back to, and the
// shared NativeRendererProps type + Notice helper.

import type { VisualJsonData, VisualLayout, VisualOption } from './types'
import {
  mapVisualData, pickGroup, measures, toNumber, formatValue, humanizeColumnLabel,
  type MappedColumn,
} from './dataUtils'
import { DisplayCell, toColDef, hasRichDisplay } from './displayCell'

/** Called when the user clicks a data row / point (drives drill-through). */
export type SelectHandler = (row: Record<string, unknown>) => void

export interface NativeRendererProps {
  jsonData: VisualJsonData
  visualLayout: VisualLayout
  /** Resolved option values (defaults to visualLayout.options). Read by
   *  renderers that have author-configurable options (e.g. the calendar's
   *  colour palette + show-text toggle). */
  optionsArr?: VisualOption[]
  /** Optional row-selection callback (used for dashboard drill-through). */
  onSelect?: SelectHandler
  /** OPT-IN: a table row was clicked (or Enter on a focused row) — the
   *  Transaction Wizard's `pick_row`. Only the table renderer reads it; absent
   *  ⇒ no behaviour change. Independent of `onSelect` (both fire when both are
   *  set). */
  onRowClick?: SelectHandler
  /** The host gives this visual NO definite height — it must size itself to
   *  its content (an embed with no block height, a smart-page tile, the
   *  runtime's nav pane). Renderers that produce real DOM can ignore it; one
   *  that hands its box to a library which MEASURES that box (ProPivot) has to
   *  know, because `height: 100%` of an auto-height parent resolves to auto and
   *  the library then measures zero and paints nothing. */
  autoHeight?: boolean
}

export function Notice({ text }: { text: string }) {
  return (
    <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-muted, #888)', textAlign: 'center' }}>
      {text}
    </div>
  )
}

// ── Html ─────────────────────────────────────────────────────────────────────
// Renders the HTML held in a content column (author-authored, dev preview), or
// falls back to a labelled list of each row's values.
export function HtmlVisual({ jsonData, visualLayout }: NativeRendererProps) {
  const md = mapVisualData(jsonData, visualLayout)
  const htmlCol = Object.keys(md.schema).find((k) => /html|content|body|template/i.test(k))
  if (htmlCol) {
    const html = md.rows.map((r) => String(r[htmlCol] ?? '')).join('\n')
    // eslint-disable-next-line react/no-danger -- author-authored content in a dev preview
    return <div style={{ padding: 12 }} dangerouslySetInnerHTML={{ __html: html }} />
  }
  if (!md.rows.length) return <Notice text="No content." />
  const cols = Object.keys(md.schema).filter((k) => k !== 'id')
  return (
    <div style={{ padding: 12, fontSize: 13 }}>
      {md.rows.map((r, i) => (
        <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--pg-border, #eee)' }}>
          {cols.map((k) => (
            <div key={k}><strong style={{ opacity: 0.7 }}>{k}:</strong> {formatValue(r[k], md.schema[k]?.type)}</div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ── Pivot (cross-tab) ────────────────────────────────────────────────────────
// rows group → row headers; columns group → column headers; first measure →
// cells (summed). Falls back to a flat grid when there's no column dimension.
// Used directly as the dependency-free fallback inside PivotVisual.
export function PivotTable({ jsonData, visualLayout }: NativeRendererProps) {
  const md = mapVisualData(jsonData, visualLayout)
  const rowDims = pickGroup(md, 'rows')
  const colDim = pickGroup(md, 'columns')[0]
  const measure = measures(md)[0]

  if (!rowDims.length || !colDim || !measure) {
    // Not enough structure to pivot — show a flat table instead.
    const cols = [...rowDims, ...(colDim ? [colDim] : []), ...measures(md)]
    const showCols = cols.length ? cols : Object.keys(md.schema).filter((k) => k !== 'id').map((k) => ({ name: k, label: humanizeColumnLabel(k), type: md.schema[k]?.type }))
    return <FlatTable rows={md.rows} cols={showCols} />
  }

  const rowKey = (r: Record<string, unknown>) => rowDims.map((d) => formatValue(r[d.name], d.type)).join(' / ')
  const rowKeys: string[] = []
  const colKeys: string[] = []
  const cells = new Map<string, number>()
  for (const r of md.rows) {
    const rk = rowKey(r)
    const ck = formatValue(r[colDim.name], colDim.type)
    if (!rowKeys.includes(rk)) rowKeys.push(rk)
    if (!colKeys.includes(ck)) colKeys.push(ck)
    const key = `${rk} ${ck}`
    cells.set(key, (cells.get(key) ?? 0) + toNumber(r[measure.name]))
  }

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={pivTh}>{rowDims.map((d) => d.label).join(' / ')}</th>
            {colKeys.map((ck) => <th key={ck} style={{ ...pivTh, textAlign: 'right' }}>{ck}</th>)}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((rk) => (
            <tr key={rk}>
              <td style={{ ...pivTd, fontWeight: 600 }}>{rk}</td>
              {colKeys.map((ck) => (
                <td key={ck} style={{ ...pivTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {cells.has(`${rk} ${ck}`) ? (cells.get(`${rk} ${ck}`) as number).toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FlatTable({ rows, cols }: { rows: Array<Record<string, unknown>>; cols: MappedColumn[] }) {
  const colDefs = cols.map((c) => toColDef(c))
  return (
    <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead>
          <tr>{cols.map((c) => <th key={c.name} style={{ ...pivTh, textAlign: c.type === 'number' ? 'right' : 'left' }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{cols.map((c, ci) => (
              <td key={c.name} style={{ ...pivTd, textAlign: c.type === 'number' ? 'right' : 'left' }}>
                {hasRichDisplay(c)
                  ? <DisplayCell value={r[c.name]} col={colDefs[ci]} row={r} columns={colDefs} />
                  : formatValue(r[c.name], c.type)}
              </td>
            ))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// textTransform/letterSpacing are pinned so a host's global `table th { text-transform:
// uppercase }` can't force-uppercase these captions — headings keep the authored case.
const pivTh = { position: 'sticky' as const, top: 0, padding: '7px 12px', borderBottom: '2px solid var(--pg-border, #e5e7eb)', background: 'var(--pg-surface, #f8f9fa)', color: 'var(--pg-heading, #111)', fontWeight: 600, whiteSpace: 'nowrap' as const, textTransform: 'none' as const, letterSpacing: 'normal' as const }
const pivTd = { padding: '6px 12px', borderBottom: '1px solid var(--pg-border, #eee)', color: 'var(--pg-text, #222)', whiteSpace: 'nowrap' as const }

// (Calendar moved to CalendarVisual.tsx — a real month-grid calendar.)
